import { Badge } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Bot, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { AgentSummary } from "@/api";
import { listAgentsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { AgentIcon } from "./AgentIcon";
import { useAgentStars } from "./useAgentStars";
import { useDeleteAgent } from "./useDeleteAgent";

/** "Mine" = agents I have a role on. */
const isMine = (a: AgentSummary) => Boolean(a.currentUserRole);

/** Build > Agents list (docs/design/web/WEB_BUILD_PATTERN_PLAN.md, phase 1). */
export function AgentsListPage() {
  const { t } = useTranslation(["build", "agents"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteAgent();
  const stars = useAgentStars();
  const { search, apply, listProps } = useListControls<AgentSummary>({
    isMine,
    sorts: [
      { value: "popular", label: t("build:list.sort.popular"), compare: (a, b) => b.usageCount - a.usageCount },
      { value: "name", label: t("build:list.sort.name"), compare: byName },
      { value: "newest", label: t("build:list.sort.newest"), compare: byNewest },
    ],
  });

  const query = useQuery(listAgentsOptions({ query: { search: search || undefined, page_size: 50 } }));
  const agents = apply(query.data?.data, { searched: true });

  return (
    <EntityListPage
      title={t("agents:title")}
      subtitle={t("agents:subtitle")}
      newLabel={t("agents:new")}
      onNew={() => navigate("/build/agents/new")}
      headerActions={<BuildWithAiButton entity="agent" />}
      {...listProps}
    >
      {agents.map((agent) => (
        <EntityRow
          key={agent.uuid}
          icon={<AgentIcon icon={agent.icon} />}
          name={agent.name}
          description={agent.description}
          meta={
            stars.isStarred(agent.uuid) && (
              <Badge variant="neutral" icon={<Star size={12} />}>
                {t("agents:star.starred")}
              </Badge>
            )
          }
          onOpen={() => navigate(`/build/agents/${agent.uuid}`)}
          menuItems={[
            {
              label: stars.isStarred(agent.uuid) ? t("agents:star.unstar") : t("agents:star.star"),
              onSelect: () => stars.toggle(agent.uuid),
            },
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/agents/${agent.uuid}`) },
            ...(agent.canManage
              ? [{ label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(agent) }]
              : []),
          ]}
        />
      ))}
      {agents.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<Bot size={32} />} title={t("agents:empty.title")} body={t("agents:empty.body")} />
        ))}
      {dialog}
    </EntityListPage>
  );
}
